"use client";

import { useQuery } from "@tanstack/react-query";

export interface SmartActivityRow {
  conditionId: string;
  smartWalletCount: number;
  smartVolume: number;
  netBuyersPct: number; // 0..1 — fraction of smart wallets that are net buyers (YES)
  avgSmartRoi: number;
  avgSmartWinRate: number;
  title?: string;
  marketSlug?: string;
}

async function fetchSmartActivity(limit: number): Promise<Map<string, SmartActivityRow>> {
  const res = await fetch(`/api/analytics/smart-activity?limit=${limit}`);
  if (!res.ok) return new Map();
  const json = await res.json();
  const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json?.markets) ? json.markets : [];
  const map = new Map<string, SmartActivityRow>();
  for (const m of arr) {
    const cid = (m.condition_id || m.conditionId || '').toLowerCase();
    if (!cid) continue;
    map.set(cid, {
      conditionId: cid,
      smartWalletCount: Number(m.smart_wallet_count || 0),
      smartVolume: Number(m.smart_volume || 0),
      netBuyersPct: Number(m.net_buyers_pct || 0),
      avgSmartRoi: Number(m.avg_smart_roi || 0),
      avgSmartWinRate: Number(m.avg_smart_win_rate || 0),
      title: m.title,
      marketSlug: m.market_slug,
    });
  }
  return map;
}

/**
 * Bulk Smart Money lookup for list views (trending, feed, markets grid, etc.).
 *
 * One fetch → Map<conditionId, row>. Every card in the list performs an O(1)
 * lookup instead of firing its own per-card fetch. Cached 5 minutes globally
 * via React Query, so N cards on the same page share a single round-trip.
 *
 * Usage:
 *   const { data: smartMap } = useSmartActivityMap();
 *   const row = smartMap?.get(conditionId.toLowerCase());
 */
export function useSmartActivityMap(limit = 500, enabled = true) {
  return useQuery<Map<string, SmartActivityRow>>({
    queryKey: ["analytics", "smart-activity-map", limit],
    queryFn: () => fetchSmartActivity(limit),
    // Predexon smart-money outage: skip the fetch under HIDE_PREDEXON so the
    // smart-money surfaces (badges, list cells, home section) hide instead of
    // rendering empty/N-A. Flip back when the Predexon plan is restored.
    enabled: enabled && process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS !== "true",
    staleTime: 5 * 60 * 1000, // 5 min
    gcTime: 15 * 60 * 1000,
    retry: false,
  });
}
